/**
 * EINGABEFELD, DAS EIN SCHLIESSEN UEBERLEBT (ASSIOEFFNENR P2-b, 13.10.2026).
 *
 * Ein Oeffnungsauftrag des Assistenten schliesst Mission Control, damit Rafael die Buehne
 * sieht. Mission Control baut dabei ab (`{showMission && …}` in App.tsx), und was dort
 * ungesendet im Kasten stand, lebte nur im React-Zustand — weg. Dieser Hook legt den Text
 * ueber `lib/entwuerfe` ab (dieselbe Ablage wie die Chat-Kaesten, inklusive Tippschutz beim
 * Neuladen) und holt ihn beim naechsten Oeffnen zurueck.
 *
 * JE FENSTER, NICHT JE URSPRUNG (Pruefurteil ASSIOEFFNENR3 P1). localStorage teilen sich alle
 * Fenster desselben Ursprungs — Rafaels Bildschirm 1 und 2. Unter einem gemeinsamen Schluessel
 * ueberschrieb Fenster B mit einem Tastendruck den Satz, den A dort abgelegt hatte. Deshalb:
 *  1. Der Schluessel traegt die Kennung DIESES Fensters (sessionStorage — ueberlebt das
 *     Neuladen desselben Tabs). Ein per window.open geoeffnetes Fenster erbt die
 *     sessionStorage seines Oeffners; nur wenn die gemerkte Kennung GENAU die des Oeffners
 *     ist, nimmt es eine neue (R4 S5: ein schon offener Bildschirm 2, der neu angesteuert
 *     wird, behaelt seine eigene).
 *  2. `setzen` ueberschreibt nie einen fremden Stand: steht in der Ablage etwas anderes als
 *     das, was dieses Feld zuletzt dort sah, legt es unter neuer Kennung daneben ab.
 *
 * KEIN ENTWURF WIRD UNSICHTBAR (Pruefurteil ASSIOEFFNENR3 R4 P1). Jede Kennung haelt ein
 * Lebenszeichen in der Ablage (`cui-entwurf-lebt:<k>`, alle 20 s). Entwuerfe von Kennungen
 * ohne frisches Lebenszeichen — Fenster zu, App neu gestartet, Fenster neu gekoppelt — und
 * Entwuerfe ohne Kennung (gemeinsamer Schluessel bis R2) uebernimmt das naechste Fenster, das
 * dieses Feld oeffnet: ist sein eigener Platz leer, wird der Text seiner; sonst haengt er
 * sichtbar abgesetzt darunter. Erst danach wird der alte Schluessel geloescht. Damit waechst
 * die Ablage auch nicht: was verwaist, wird beim naechsten Oeffnen eingesammelt.
 *
 * `ungesicherteEntwuerfe()` nennt Felder, deren Text gerade NICHT in der Ablage steht (Speicher
 * voll oder gesperrt). Wer ein solches Feld abbauen will, darf es nicht: App.tsx laesst
 * Mission Control dann offen und die Oeffnung meldet den Grund.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { entwurfSchluessel, leseEntwurf, schreibeEntwurf } from './entwuerfe';

const FENSTER_MERKER = 'cui-entwurf-fenster';
const LEBT_PRAEFIX = 'cui-entwurf-lebt:';
/**
 * Wie oft ein Fenster sein Lebenszeichen erneuert, und ab wann eine Kennung als tot gilt.
 * Die Frist muss ueber dem gedrosselten Takt liegen: Chrome/Electron lassen Intervalle in
 * Hintergrund-Tabs hoechstens einmal pro Minute laufen. Deshalb Frist ≥ 3 × Takt und
 * > 60 s, dazu ein Lebenszeichen bei jedem Sichtbarwerden (Pruefurteil R5, X4).
 */
export const LEBENSZEICHEN_TAKT_MS = 20_000;
export const LEBENSZEICHEN_FRIST_MS = 90_000;
export const HINTERGRUND_DROSSEL_MS = 60_000;
/** Trennzeile, unter der ein uebernommener Entwurf an einen vorhandenen angehaengt wird. */
export const UEBERNAHME_TRENNER = '\n\n— Entwurf aus einem geschlossenen Fenster —\n';

let kennung: string | null = null;
let takt: ReturnType<typeof setInterval> | null = null;

function lebenszeichen(): void {
  if (!kennung) return;
  try { localStorage.setItem(LEBT_PRAEFIX + kennung, String(Date.now())); } catch { /* ohne Speicher keine Entwuerfe */ }
}

function kennungSetzen(k: string): string {
  kennung = k;
  try { sessionStorage.setItem(FENSTER_MERKER, k); } catch { /* ohne sessionStorage: nur fuer diese Seite */ }
  lebenszeichen();
  if (!takt && typeof window !== 'undefined') {
    takt = setInterval(lebenszeichen, LEBENSZEICHEN_TAKT_MS);
    document.addEventListener('visibilitychange', lebenszeichen);
  }
  return k;
}

/**
 * BEIM LADEN DER APP, NICHT ERST MIT MISSION CONTROL (Pruefurteil R5 P1). Ein Fenster, das
 * neu laedt (Rebuild) und Mission Control nicht oeffnet, hielt sonst kein Lebenszeichen und
 * galt nach 90 s als tot — sein Satz wanderte in ein anderes Fenster. main.tsx ruft das.
 */
export function entwurfsLebenszeichenStarten(): string { return fensterKennung(); }

function neueKennung(): string {
  return kennungSetzen(typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10));
}

/** Ist `gemerkt` die Kennung des Oeffners — also nur geerbt (window.open, Erstladen)? */
function vomOeffnerGeerbt(gemerkt: string): boolean {
  try {
    const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!window.opener || nav?.type !== 'navigate') return false;
    return (window.opener as Window).sessionStorage.getItem(FENSTER_MERKER) === gemerkt;
  } catch { return false; }  // Oeffner nicht lesbar (anderer Ursprung, zu): dann gehoert die Kennung diesem Fenster
}

/** Kennung dieses Fensters fuer Entwurfsschluessel. */
export function fensterKennung(): string {
  if (kennung) return kennung;
  let gemerkt: string | null = null;
  try { gemerkt = sessionStorage.getItem(FENSTER_MERKER); } catch { /* s. o. */ }
  if (gemerkt && !vomOeffnerGeerbt(gemerkt)) return kennungSetzen(gemerkt);
  return neueKennung();
}

/** Nur fuer Tests: Kennung vergessen, als waere die Seite neu geladen. */
export function fensterKennungVergessen(): void {
  kennung = null;
  if (takt) { clearInterval(takt); takt = null; document.removeEventListener('visibilitychange', lebenszeichen); }
}

const schluesselFuer = (name: string, fenster: string) => entwurfSchluessel(`${name}@${fenster}`);
const ENTWURF_PRAEFIX = entwurfSchluessel('x').slice(0, -1);  // 'cui-entwurf-'

function lebt(k: string, jetzt: number): boolean {
  if (k === kennung) return true;
  try {
    const t = Number(localStorage.getItem(LEBT_PRAEFIX + k));
    return Number.isFinite(t) && jetzt - t < LEBENSZEICHEN_FRIST_MS;
  } catch { return true; }  // nicht pruefbar: lieber liegen lassen als fremd uebernehmen
}

/** Text, der gerade in einem eingehaengten Feld steht — je Ablageschluessel. */
const imFeld = new Map<string, string>();

/**
 * Entwuerfe toter Kennungen (und ohne Kennung) fuer Namen, die mit `praefix` beginnen (oder
 * genau `name` sind), unter die eigene Kennung holen; danach den alten Schluessel loeschen.
 * Ein eingehaengtes eigenes Feld wird nicht angefasst — sein Zustand waere sonst veraltet.
 * Liefert die Namen, die etwas uebernommen haben.
 */
export function uebernimmVerwaiste(praefix: string, genau = false): string[] {
  const eigene = fensterKennung();
  const jetzt = Date.now();
  const uebernommen: string[] = [];
  const kandidaten: Array<{ name: string; schluessel: string; text: string }> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(ENTWURF_PRAEFIX) || k.startsWith(LEBT_PRAEFIX)) continue;
      const rest = k.slice(ENTWURF_PRAEFIX.length);
      const at = rest.lastIndexOf('@');
      const name = at < 0 ? rest : rest.slice(0, at);
      const fenster = at < 0 ? null : rest.slice(at + 1);
      if (genau ? name !== praefix : !name.startsWith(praefix)) continue;
      if (fenster === eigene || (fenster && lebt(fenster, jetzt))) continue;
      const text = leseEntwurf(k);
      if (text.trim()) kandidaten.push({ name, schluessel: k, text });
      else localStorage.removeItem(k);
    }
  } catch { return []; }
  for (const { name, schluessel, text } of kandidaten) {
    const ziel = schluesselFuer(name, eigene);
    if (imFeld.has(ziel)) continue;
    const vorhanden = leseEntwurf(ziel);
    const neu = vorhanden.trim() ? vorhanden + UEBERNAHME_TRENNER + text : text;
    schreibeEntwurf(ziel, neu);
    if (leseEntwurf(ziel) !== neu) continue;      // nicht gesichert: Quelle stehen lassen, nie verwerfen
    try { localStorage.removeItem(schluessel); } catch { /* bleibt liegen, wird beim naechsten Mal wieder versucht */ }
    uebernommen.push(name);
  }
  // Lebenszeichen toter Kennungen ohne Entwurf aufraeumen (Obergrenze der Ablage).
  try {
    const tote: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(LEBT_PRAEFIX) && !lebt(k.slice(LEBT_PRAEFIX.length), jetzt)) tote.push(k);
    }
    tote.forEach(k => localStorage.removeItem(k));
  } catch { /* s. o. */ }
  return uebernommen;
}

export function useGesicherterEntwurf(name: string): [string, (text: string) => void] {
  const [schluessel, setSchluessel] = useState(() => { uebernimmVerwaiste(name, true); return schluesselFuer(name, fensterKennung()); });
  const [text, setText] = useState(() => leseEntwurf(schluessel));
  /** Was dieses Feld zuletzt in der Ablage gesehen oder dort hinterlegt hat. */
  const eigenerStand = useRef(text);
  // Wechselt der Name (anderes Gespraech), gilt dessen Entwurf.
  const vorigerName = useRef(name);
  useEffect(() => {
    if (vorigerName.current === name) return;
    vorigerName.current = name;
    uebernimmVerwaiste(name, true);
    const k = schluesselFuer(name, fensterKennung());
    const t = leseEntwurf(k);
    eigenerStand.current = t;
    setSchluessel(k);
    setText(t);
  }, [name]);
  useEffect(() => {
    imFeld.set(schluessel, text);
    return () => { imFeld.delete(schluessel); };
  }, [schluessel, text]);
  const setzen = useCallback((neu: string) => {
    setText(neu);
    let k = schluessel;
    if (leseEntwurf(k) !== eigenerStand.current) {
      // Fremder Stand unter diesem Schluessel: nicht ueberschreiben, daneben ablegen.
      k = schluesselFuer(name, neueKennung());
      setSchluessel(k);
    }
    schreibeEntwurf(k, neu);
    eigenerStand.current = leseEntwurf(k);
  }, [schluessel, name]);
  return [text, setzen];
}

/** Gibt es fuer dieses Fenster einen Entwurf (auch einen eben uebernommenen)? Mission Control oeffnet das Feld dann. */
export function hatEntwurf(name: string): boolean {
  uebernimmVerwaiste(name, true);
  return !!leseEntwurf(schluesselFuer(name, fensterKennung())).trim();
}

/** Namen (ohne Fensterkennung) mit Entwurf dieses Fensters, die mit `praefix` beginnen — nach Uebernahme. */
export function entwurfsNamen(praefix: string): string[] {
  uebernimmVerwaiste(praefix);
  const ende = `@${fensterKennung()}`;
  const start = ENTWURF_PRAEFIX + praefix;
  const namen: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(start) || !k.endsWith(ende) || !leseEntwurf(k).trim()) continue;
      namen.push(k.slice(ENTWURF_PRAEFIX.length, -ende.length));
    }
  } catch { /* ohne Speicher keine Entwuerfe */ }
  return namen;
}

/** Ablageschluessel eingehaengter Felder mit Text, der nicht gesichert ist. */
export function ungesicherteEntwuerfe(): string[] {
  return [...imFeld].filter(([k, t]) => t.trim() && leseEntwurf(k) !== t).map(([k]) => k);
}
